import { Form, Input, Modal } from 'antd';
import React from 'react';

import { useRenameFolder } from '@/api';
import { usePermission } from '@/hooks/usePermission';
import { showMessage } from '@/shared/utils';

interface RenameFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newName: string) => void;
  folderPath: string;
  currentName: string;
}

export function RenameFolderModal({
  isOpen,
  onClose,
  onSuccess,
  folderPath,
  currentName,
}: RenameFolderModalProps): React.JSX.Element {
  const [form] = Form.useForm();
  const { isMAAdmin } = usePermission();
  const [isLoading, setIsLoading] = React.useState(false);
  const renameFolderMutation = useRenameFolder();

  React.useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ name: currentName });
    }
  }, [isOpen, currentName, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      await renameFolderMutation.mutateAsync({
        folderPath,
        name: values.name,
      });
      showMessage('success', 'Ordner erfolgreich umbenannt');
      onSuccess(values.name);
      onClose();
    } catch (error: any) {
      if (error.response?.status === 409) {
        form.setFields([
          {
            name: 'name',
            errors: [error.response.data.message],
          },
        ]);
      } else {
        showMessage('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isMAAdmin) {
    return <></>;
  }

  return (
    <Modal
      title='Ordner umbenennen'
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      destroyOnHidden
    >
      <Form form={form} layout='vertical' initialValues={{ name: currentName }}>
        <Form.Item
          name='name'
          rules={[
            { required: true, message: 'Bitte geben Sie einen Namen ein' },
            { max: 255, message: 'Es sind maximal 255 Zeichen erlaubt' },
            {
              pattern: /^[^\\/:*?"<>|]+$/,
              message: 'Name darf keine Sonderzeichen enthalten (\\ / : * ? " < > |)',
            },
          ]}
        >
          <Input placeholder='Neuer Name' />
        </Form.Item>
      </Form>
    </Modal>
  );
}
